import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  Box,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { acknowledgeAwsEvent } from "../../Services";

/**
 * AcknowledgeDialog
 * - Handles its own API call to acknowledge an event.
 * - On success, calls onSuccess({ eventId, comment, date, user })
 */
const AcknowledgeDialog = ({
  open,
  eventId,
  defaultComment = "",
  username,
  onSuccess,
  onClose,
}) => {
  const [comment, setComment] = useState(defaultComment);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setComment(defaultComment || "");
    }
  }, [open, defaultComment]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const finalComment = (comment || "").trim();
      await acknowledgeAwsEvent({
        releaseId: eventId,
        comment: finalComment,
        username,
      });

      onSuccess?.({
        eventId,
        comment: finalComment,
        date: new Date().toISOString(),
        user: username,
      });
    } catch (e) {
      console.error("Acknowledge failed:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 1.5,
          width: "min(760px, 96vw)",
          boxSizing: "border-box",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.25, pr: 6, py: 3 }}>
        {`Acknowledge the AWS alert - ${eventId || ""}`}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#666" }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0.5, pb: 2.5, px: { xs: 2, sm: 3 } }}>
        <Typography sx={{ color: "#6b6b6b", mb: 5, fontSize: 13 }}>
          This will update the status to <i>Acknowledged</i>, so that all
          service owners and admins will be informed.
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "110px minmax(0,1fr)" },
            columnGap: { xs: 0, sm: 1.5 },
            rowGap: 3,
            alignItems: "center",
            px: 6,
            pb: 4,
          }}
        >
          {/* Status (read-only) */}
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Status :</Typography>
          <TextField
            size="small"
            value="Acknowledged"
            variant="outlined"
            InputProps={{ readOnly: true }}
            sx={{
              width: "100%",
              maxWidth: { sm: 324 },
              "& .MuiOutlinedInput-root": {
                height: 30,
                bgcolor: "#F2F2F2",
                borderRadius: 1,
                "& fieldset": { borderColor: "#DADADA" },
                "&:hover fieldset, &.Mui-focused fieldset": { borderColor: "#CFCFCF" },
              },
              "& .MuiOutlinedInput-input": { py: 0, px: 1.25, fontSize: 13, lineHeight: "30px" },
            }}
          />

          {/* Comment */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              alignSelf: { xs: "auto", sm: "start" },
              mt: { xs: 0, sm: 0.25 },
            }}
          >
            Comment :
          </Typography>
          <TextField
            size="small"
            multiline
            variant="outlined"
            placeholder="Add a comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{
              width: "100%",
              maxWidth: { sm: 420 },
              "& .MuiOutlinedInput-root": {
                height: 60,
                borderRadius: 1,
                alignItems: "start",
                "& fieldset": { borderColor: "#DADADA" },
                "&:hover fieldset, &.Mui-focused fieldset": { borderColor: "#CFCFCF" },
              },
              "& .MuiOutlinedInput-input": { fontSize: 13, p: 1 },
              "& .MuiOutlinedInput-inputMultiline": { p: 1 },
              "& textarea": { height: "60px !important", resize: "none" },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={saving}
            sx={{
              px: 3,
              bgcolor: "#000",
              "&:hover": { bgcolor: "#222" },
              borderRadius: 1,
              textTransform: "none",
            }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AcknowledgeDialog;
